import { useEffect, useState } from "react";
import {
  adminPatchReservation,
  adminReservations,
  adminResend,
  type AdminReservation,
} from "../../lib/api";
import { useI18n } from "../../i18n";
import { EVENT_DAYS, ROUND_TIMES } from "../../../shared/event";
import { Download } from "../Icon";

function slotShort(id: string): string {
  const m = id.match(/^(\d{4}-\d{2}-\d{2})_R([123])$/);
  if (!m) return id;
  const rt = ROUND_TIMES.find((r) => r.round === Number(m[2]));
  return `${m[1].slice(5)} ${rt?.start ?? ""}`;
}

export function ReservationsTab() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [slot, setSlot] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState<AdminReservation[]>([]);
  const [loading, setLoading] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [editSize, setEditSize] = useState(1);
  const [editStatus, setEditStatus] = useState("confirmed");
  const [msg, setMsg] = useState<Record<string, string>>({});

  function load() {
    setLoading(true);
    adminReservations({ q, slot, status })
      .then(setRows)
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [q, slot, status]); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(r: AdminReservation) {
    setEditId(r.id);
    setEditSize(r.party_size);
    setEditStatus(r.status);
  }

  async function saveEdit(id: string) {
    try {
      await adminPatchReservation(id, { party_size: editSize, status: editStatus });
      setEditId(null);
      load();
    } catch {
      setMsg((m) => ({ ...m, [id]: "✗" }));
    }
  }

  async function resend(id: string) {
    setMsg((m) => ({ ...m, [id]: "…" }));
    try {
      const r = await adminResend(id);
      setMsg((m) => ({ ...m, [id]: r.email_status === "sent" ? "✓ sent" : `✗ ${r.email_status}` }));
      load();
    } catch {
      setMsg((m) => ({ ...m, [id]: "✗" }));
    }
  }

  const allSlots = EVENT_DAYS.flatMap((d) =>
    ROUND_TIMES.map((r) => ({ id: `${d.date}_R${r.round}`, label: `${d.date.slice(5)} ${r.start}` })),
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="field max-w-xs flex-1"
          placeholder={t("admin.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="field w-auto" value={slot} onChange={(e) => setSlot(e.target.value)}>
          <option value="">{t("admin.filterSlot")}</option>
          {allSlots.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <select className="field w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">—</option>
          <option value="confirmed">{t("manage.confirmed")}</option>
          <option value="cancelled">{t("manage.cancelled")}</option>
        </select>
        <a href="/api/admin/reservations.csv" className="btn-ghost text-sm">
          <Download className="h-4 w-4" /> {t("admin.exportCsv")}
        </a>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl bg-white/80 ring-1 ring-black/5">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-black/5 text-xs uppercase text-ink/50">
            <tr>
              <th className="p-2">Code</th>
              <th className="p-2">{t("admin.name")}</th>
              <th className="p-2">{t("admin.phone")}</th>
              <th className="p-2">{t("admin.email")}</th>
              <th className="p-2">{t("admin.slot")}</th>
              <th className="p-2">{t("admin.size")}</th>
              <th className="p-2">{t("admin.status")}</th>
              <th className="p-2">{t("admin.emailStatus")}</th>
              <th className="p-2">{t("admin.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const editing = editId === r.id;
              return (
                <tr key={r.id} className="border-t border-black/5 align-middle">
                  <td className="p-2 font-mono text-xs">{r.code}</td>
                  <td className="p-2 font-semibold">{r.name}</td>
                  <td className="p-2 text-ink/70">{r.phone}</td>
                  <td className="p-2 text-ink/70">{r.email}</td>
                  <td className="p-2 whitespace-nowrap">{slotShort(r.slot_id)}</td>
                  <td className="p-2">
                    {editing ? (
                      <input
                        type="number"
                        min={1}
                        className="field w-16 px-2 py-1"
                        value={editSize}
                        onChange={(e) => setEditSize(Number(e.target.value))}
                      />
                    ) : (
                      r.party_size
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <select className="field px-2 py-1" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                        <option value="confirmed">{t("manage.confirmed")}</option>
                        <option value="cancelled">{t("manage.cancelled")}</option>
                      </select>
                    ) : (
                      <span className={r.status === "cancelled" ? "text-ink/40" : "text-green-600"}>{r.status}</span>
                    )}
                  </td>
                  <td className="p-2 text-xs text-ink/50">{r.email_status}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      {editing ? (
                        <>
                          <button onClick={() => saveEdit(r.id)} className="rounded-lg bg-[var(--theme-main)] px-2 py-1 text-xs font-bold text-white">
                            {t("admin.save")}
                          </button>
                          <button onClick={() => setEditId(null)} className="text-xs text-ink/50">
                            {t("common.cancel")}
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(r)} className="text-xs font-semibold text-[var(--theme-deep)]">
                            {t("admin.edit")}
                          </button>
                          <button onClick={() => resend(r.id)} className="text-xs text-ink/50 hover:text-[var(--theme-deep)]">
                            {t("admin.resend")}
                          </button>
                        </>
                      )}
                      {msg[r.id] && <span className="text-xs text-ink/50">{msg[r.id]}</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <div className="p-6 text-center text-ink/40">—</div>}
      </div>
    </div>
  );
}
